'use client';

import {useCallback, useState} from 'react';

import {useI18n} from '@udemy-v2/i18n';
import {PlayOverlay} from '@udemy-v2/react-structure-components';

import {ConsentGate} from './consent-gate.react-component';
import {hasExternalVideoConsent, setExternalVideoConsent} from './external-video-consent';

import {getAssetBase} from '@/lib/asset-base';

import styles from './walkthrough-media.module.scss';

const ASSET_BASE = getAssetBase('platform-showcase/v1');

/**
 * Screenshot of the community UI. Doubles as the video poster and — while the
 * walkthrough video is not ready — as the standalone `WalkthroughImage`.
 */
const IMAGE_SRC = `${ASSET_BASE}/community-preview.webp`;

/**
 * Public YouTube video ID for the platform walkthrough, embedded via the
 * privacy-enhanced `youtube-nocookie.com` domain. Fixed by design — not
 * user-supplied, so no parsing/validation is needed here (contrast the
 * reference app's `VideoIntroSection`, which accepts an instructor-pasted URL).
 */
const VIDEO_ID = '9tusoKSh0AU';
const EMBED_URL = `https://www.youtube-nocookie.com/embed/${VIDEO_ID}`;

/**
 * Static stand-in for `WalkthroughVideo`: the same screenshot, at the same size
 * and position, with no play affordance. Rendered by `PlatformShowcase` because
 * the walkthrough video does not exist yet — swap that call back to
 * `<WalkthroughVideo />` once it does, and delete nothing else.
 */
export function WalkthroughImage() {
    const {gettext} = useI18n();

    return (
        /* eslint-disable-next-line @next/next/no-img-element */
        <img
            className={styles['poster']}
            src={IMAGE_SRC}
            alt={gettext('The instructor community platform')}
            loading="lazy"
            decoding="async"
        />
    );
}

/**
 * The walkthrough video: a screenshot facade that swaps for a live YouTube
 * embed once third-party content consent is given (poster → consent → iframe,
 * the same pattern as the reference app's biography video, minus the editing
 * controls since the video here is fixed rather than instructor-supplied).
 *
 * Sized and positioned by its container — `PlatformShowcase`'s `.media`, which
 * owns the aspect ratio, corner radius and shadow.
 */
export function WalkthroughVideo() {
    const {gettext} = useI18n();
    const [isPlaying, setIsPlaying] = useState(false);
    const [consentPending, setConsentPending] = useState(false);

    // No request reaches youtube-nocookie.com until consent exists for this
    // session, so the gate stands between the poster click and the iframe.
    const handlePlay = useCallback(() => {
        if (hasExternalVideoConsent()) {
            setIsPlaying(true);
        } else {
            setConsentPending(true);
        }
    }, []);

    const handleConsentAccept = useCallback((rememberChoice: boolean) => {
        if (rememberChoice) {
            setExternalVideoConsent();
        }
        setConsentPending(false);
        setIsPlaying(true);
    }, []);

    const handleConsentDismiss = useCallback(() => setConsentPending(false), []);

    if (isPlaying) {
        return (
            <iframe
                className={styles['iframe']}
                src={`${EMBED_URL}?autoplay=1&rel=0&modestbranding=1`}
                title={gettext('Instructor community platform walkthrough')}
                loading="lazy"
                referrerPolicy="strict-origin-when-cross-origin"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
            />
        );
    }

    return (
        <>
            <button
                type="button"
                className={styles['play-button']}
                onClick={handlePlay}
                aria-label={gettext('Play the community platform walkthrough video')}
            >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                    className={styles['poster']}
                    src={IMAGE_SRC}
                    alt=""
                    loading="lazy"
                    decoding="async"
                />
                <PlayOverlay size="xxlarge" />
            </button>
            {consentPending && (
                <ConsentGate onAccept={handleConsentAccept} onDismiss={handleConsentDismiss} />
            )}
        </>
    );
}
